import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Save, Loader2, Plus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";
import { ApprovalLocationPrices } from "@/components/enhancer/ApprovalLocationPrices";

// Enhancers self-select which official center services they offer; the service
// name & pricing rules stay Council-controlled. They add a personal approach note.
export default function CenterApprovalsPanel() {
  const qc = useQueryClient();
  const [picking, setPicking] = useState("");
  const [adding, setAdding] = useState(false);
  const { data = [], isLoading } = useQuery({ queryKey: ["my-center-approvals"], queryFn: () => api.get("/enhancer/me/center-approvals").then((r) => r.data) });
  const { data: avail = { eligible: false, services: [] } } = useQuery({ queryKey: ["center-services-available"], queryFn: () => api.get("/enhancer/me/center-services/available").then((r) => r.data) });
  const eligible = avail.eligible;
  const available = avail.services || [];

  const addService = async () => {
    if (!picking) return;
    setAdding(true);
    try {
      const { data: created } = await api.post("/enhancer/me/center-approvals", { service_id: picking });
      toast.success(created?.approved ? "Added to your profile." : "Request sent — the center manager will review it.");
      setPicking("");
      qc.invalidateQueries({ queryKey: ["my-center-approvals"] });
      qc.invalidateQueries({ queryKey: ["center-services-available"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  if (isLoading) return null;
  return (
    <div className="mb-8" data-testid="enh-center-approvals">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-1 flex items-center gap-2"><ShieldCheck size={20} className="text-[#5C8A72]" /> Center services you offer</h2>
      <p className="text-xs text-[#4A5D6B] mb-4">Select from the official EYW service library. Service names are set by the Council — personalize your approach and set your own price at each location below.</p>

      {!eligible && (
        <div className="rounded-xl border border-[#E5E0D8] bg-white p-4 text-sm text-[#4A5D6B] mb-4" data-testid="center-not-eligible">
          You're not yet approved at a flagship center, so adding a service sends a <b>request to the center manager</b> — it appears on your profile once they approve.{" "}
          <Link to="/dashboard" className="text-[#144F75] font-medium hover:underline">Apply from Places → Our Centers</Link> for instant self-add.
        </div>
      )}
      {available.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-2 mb-4 max-w-xl" data-testid="center-service-picker">
          <select value={picking} onChange={(e) => setPicking(e.target.value)} data-testid="center-service-select" className="eyw-input flex-1">
            <option value="">{eligible ? "Add a center service you offer…" : "Request to offer a center service…"}</option>
            {available.map((s) => <option key={s.id} value={s.id}>{s.title}{s.reporting_category ? ` · ${s.reporting_category}` : ""}</option>)}
          </select>
          <button onClick={addService} disabled={!picking || adding} data-testid="center-service-add" className="eyw-btn !py-2 text-sm disabled:opacity-50 shrink-0">
            {adding ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} {eligible ? "Add" : "Request"}
          </button>
        </div>
      )}
      {data.length === 0 ? (
        <p className="text-sm text-[#4A5D6B]">You haven't added any center services yet. Pick one above to list it on your profile.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.map((a) => <ApprovalCard key={a.id} approval={a} />)}
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ approval }) {
  const qc = useQueryClient();
  const [bio, setBio] = useState(approval.bio || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/enhancer/me/center-approvals/${approval.id}/bio`, { bio }); toast.success("Saved — your changes are live"); qc.invalidateQueries({ queryKey: ["my-center-approvals"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove "${approval.service_title}" from your profile?`)) return;
    try {
      await api.delete(`/enhancer/me/center-approvals/${approval.id}`);
      toast.success("Removed from your profile.");
      qc.invalidateQueries({ queryKey: ["my-center-approvals"] });
      qc.invalidateQueries({ queryKey: ["center-services-available"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="eyw-card p-5" data-testid={`enh-approval-${approval.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg text-[#1A242B]">{approval.service_title}</h3>
          <p className="text-xs text-[#8FA89B]">{approval.reporting_category}{approval.price_override != null ? ` · ${money(approval.price_override)}` : ""}{approval.can_virtual ? " · virtual ok" : ""}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {approval.approved ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">Live</span>
          ) : approval.rejected ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-600">Declined</span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D98E73]/15 text-[#D98E73]">Pending approval</span>
          )}
          <button onClick={remove} data-testid={`approval-remove-${approval.id}`} title="Remove from my profile" className="text-[#B9C4BD] hover:text-red-500"><Trash2 size={15} /></button>
        </div>
      </div>
      <textarea rows={2} value={bio} onChange={(e) => setBio(e.target.value)} data-testid={`approval-bio-${approval.id}`} placeholder="Your approach / style for this offering (optional)" className="eyw-input w-full text-sm mt-3 resize-none" />
      <button onClick={save} disabled={saving} data-testid={`approval-bio-save-${approval.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs mt-2">{saving ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save note</button>
      <ApprovalLocationPrices approval={approval} />
    </div>
  );
}
